import { useEffect, useRef, useState } from "react";
import type { Entity, ID, PaginateResourceResponse } from "types";
import { PER_PAGE_SIZE } from "consts/transactions";
import { when } from "util/support";
import useFetchResource from "hooks/useFetchResource";
import type { ManageOpenBankingResourcePivotProps } from "./types";

export default function useManageOpenBankingResourcePivot<
    TResource extends Entity,
    TPivotEntity extends Entity
>({
    resource,
    groupBy,
    fetchPivots
}: Pick<
    ManageOpenBankingResourcePivotProps<TResource, TPivotEntity>,
    | 'resource'
    | 'groupBy'
    | 'fetchPivots'
>) {
    const [isPivotsLoading, setPivotsLoading] = useState(false);
    const [pivotMap, setPivotMap] = useState<Map<ID, TPivotEntity[]>>(() => new Map());

    const request = useFetchResource({
        setLoading: setPivotsLoading,
        shouldFetchAll: true,
        perPage: PER_PAGE_SIZE
    });

    const helpers = {
        resource,
        pivots: pivotMap,
        groupBy,
        setPivot: (resourceId: ID, pivot: TPivotEntity) => setPivotMap(state => {
            const newState = new Map(state);
            return newState.set(resourceId, [
                ...newState.get(resourceId) || [],
                pivot
            ]);
        }),
        deletePivotResourceId: (resourceId: ID) => setPivotMap(state => {
            const newState = new Map(state);
            newState.delete(resourceId);
            return newState;
        }),
        getPivotByResourceId(resourceId: ID) {
            return pivotMap.get(resourceId)?.[0];
        },
        fetchPivots: ({ pki }: Pick<TResource, 'pki'>) => request(() => fetchPivots({ pki })),
        resetState: () => {
            setPivotMap(new Map());
            setPivotsLoading(false);
        }
    };
    const helpersRef = useRef(helpers);
    helpersRef.current = helpers;
    const pki = resource?.pki;

    useEffect(() => {
        let isMounted = true;
        const {
            resetState,
            fetchPivots,
            groupBy,
        } = helpersRef.current;

        if (!pki) {
            resetState();
            return;
        }

        fetchPivots({ pki })
            .then(({ data }: PaginateResourceResponse<TPivotEntity>) => when(isMounted, () => (
                setPivotMap(
                    // TODO: remove after TS upgrade
                    (Map as any).groupBy(data.data, groupBy)
                )
            )));

        return () => {
            isMounted = false;
        };
    }, [pki]);

    return {
        apiRef: helpersRef,
        isPivotsLoading
    };
};
